/* eslint-disable max-depth */
/* eslint-disable max-lines */
/* eslint-disable react-func/max-lines-per-function */
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useGetState, useLatest } from 'ahooks';
import { debounce, differenceWith, isEqual } from 'lodash-es';
import { klona } from 'klona';
import { useSelector } from 'react-redux';
import { Icon } from '@fuxi/eevee-icon';
import MassagePointImg from '@/assets/image/massage-point.png';
import { MassageControlValueContext } from '@/pages/service/massage-control';
import { selectCurrentServingOrder } from '@/store/service';
import MassagePointActiveImg from '@/assets/image/massage-point-active.png';
import { Canvas2DViewer } from '@/components/body-2d-viewer/viewer';
import { storagePrefixFixedV2, storagePrefixV2 } from '@/constant';
import When from '../When';
import { ModelSolution } from './model-config';
import {
  allAbdomenMoxInfo,
  AnimationArrowConfig,
  BUFFET_PLAN_STEP_2_TIPS,
  FullMassageBgConfig,
  FullMassageBgConfigWith16Point,
  FullMassagePointConfig,
  FullMassagePointConfigWith10Point,
  FullMassageWith16PointConfig,
  lvShoulderMassageV2ActionId2Times,
  lvWaistMassageActionId2Times,
  MassageActivePointOffetSize,
  MassagePointSize,
  MassageZhaiBgConfig,
  MassageZhaiPointsConfig,
  ShouldeLvMassageBgConfigV2,
  ShouldeLvMassageBgConfigV2Optimize,
  ShoulderLvMassagePointConfigV2,
  ShoulderLvMassagePointConfigV2Optimize,
  ShoulderLvMassagePointConfigV2OptimizeRoute,
  SOLUTION_TO_MASSAGE_POINT,
  WaistActionId2Times,
  WaistLvMassageBg,
  WaistLvMassageBgStep2,
  WaistLvMassagePointConfig,
  WaistLvMassagePointRouteConfig,
  WaistMassageBgConfig,
  WaistMassagePointConfig,
  ZhaiActionId2Times,
  ZhaiTeacherRoutePoints,
} from './utils';
import RFAnimation, { RFAnimationTpye } from './rf-animation';
import MoxAnimation from './mox-animation';
import cx from './index.module.scss';
import PlainLabel from './plain-label';

interface Props {
  modelSolution: Massage.ModelSolution | Massage.DynamicSolution;
  // 是否只渲染模型底图
  onlyRenderSolutionMainBg?: boolean;
  bodyNodeList: Palpation.PointTypeWithDirection[];
  isMatrixMox?: boolean;
  onRenderModelOK?: VoidFunc;
  onRenderPointOK?: VoidFunc;
  isInIntensifying?: boolean;
}

export const IAMGE_URL_BACK: Record<Customer.Gender, string> = {
  Male: storagePrefixV2 + '/massage-note/model-bg-male-v3.png',
  Female: storagePrefixV2 + '/massage-note/model-bg-female-v3.png',
};

export const IAMGE_URL_FRONT: typeof IAMGE_URL_BACK = {
  Male: storagePrefixV2 + '/front-bg-male.png',
  Female: storagePrefixFixedV2 + '/model-texture/human-female-front-2.png',
};

export interface Model2DRefAttrs {
  screenshot: () => string;
  clearActiveNode: VoidFunc;
  resetNodeHighlight: VoidFunc;
  resetSplineHighlight: VoidFunc;
  resetDynamicSolution: VoidFunc;
  clearAllPoints: VoidFunc;
  setMatrixMoxibustionArea: (areas: MatrixMoxibustionArea[], config?: { isAbdomen: boolean }) => Promise<void>;
  drawDynamicSolution: (solution: Massage.DynamicSolution, config: Massage.DynamicActionConfig) => Promise<void>;
  renderCustomLineCoords: (
    coords: Array<{
      lineId: string;
      state: 'RUNNING' | 'PENDING' | 'DONE';
      coord: number[];
    }>
  ) => void;
  renderPreSetSplineHitInfo: (
    coords: Array<{
      lineId: number;
      state: 'RUNNING' | 'PENDING' | 'DONE';
    }>
  ) => void;
  activeCustomLine: (lineId: string) => void;
  handleRenderMassageNodule: (config: Massage.DynamicActionConfig) => void;
}

export type MatrixMoxibustionArea = {
  // [note] image6 和 image7 代表腹部艾灸配置
  name: 'image1' | 'image2' | 'image3' | 'image4' | 'image5' | 'image6' | 'image7';
  opacity?: number;
};

/** 需要渲染结节的方案 */
const PLAN_SHOULD_RENDER_NODE_POINT: Massage.ModelSolution[] = [
  'shoulder',
  'back',
  'shock-shoulder-deji',
  'shock-waist-deji',
  'shock-waist-teacher-zhai',
  'shock-shoulder-teacher-zhai',
  'massage-shoulder',
  'massage-waist',
  'custom-draw-line',
  'shock-shoulder-yst-1',
  'shock-waist-yst-1',
  'shock-waistback-yst-1',
  'shock-waistback-yst-2',
];

const SPLINE_NAME_TO_CIRCLE_NAME = {
  shoulder: {
    line1: 'circle1',
    line2: 'circle1',
    line3: 'circle3',
    line4: 'circle1',
    line5: 'circle2',
    line6: 'circle2',
    line7: 'circle4',
    line8: 'circle2',
  },
  back: {
    line1: 'circle2',
    line2: 'circle1',
    line4: 'circle2',
    line5: 'circle3',
    line6: 'circle4',
    line8: 'circle3',
  },
  'shock-shoulder-teacher-zhai': {
    line1: 'circle2',
    line2: 'circle1',
    line3: 'circle2',
    line4: 'circle3',
    line5: 'circle4',
    line6: 'circle3',
  },
  'shock-waist-teacher-zhai': {
    line1: 'circle2',
    line2: 'circle1',
    line3: 'circle3',
    line4: 'circle4',
  },
};

/** 需要actions动态渲染的方案 */
export const shoulderHaveDynamicActionsPlan = [
  'massage-yzc',
  'massage-yzc-dynamic',
  'waist-massage-yzc-dynamic',
  'massage-yzc-zhai-dynamic',
  'massage-shoulder-lv-dynamic',
  'massage-shoulder-lv-v2-dynamic',
  'massage-yzc-10-dynamic',
];

const transfromPointCoord = (coords: number[]) => coords?.map(item => item + MassageActivePointOffetSize);

const DASH_LINE_CONFIG = {
  dash: [6, 6],
  opacity: 0.7,
};
const DASH_LINE_OPACITY_CONFIG = {
  dash: [6, 6],
  opacity: 0.3,
};

const Model2D = forwardRef<Model2DRefAttrs, Props>(
  (
    {
      modelSolution,
      onRenderModelOK,
      onRenderPointOK,
      bodyNodeList,
      isInIntensifying,
      isMatrixMox = false,
      onlyRenderSolutionMainBg = false,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const viewerRef = useRef<Canvas2DViewer>(undefined);
    const currentServingOrder = useSelector(selectCurrentServingOrder);
    const latestCustomerGender = useLatest(currentServingOrder?.customerGender);
    const { productInfo, currentMassageType } = useContext(MassageControlValueContext);
    const hasInitViewer = useRef(false);
    const currentBodyImageRef = useRef<'front' | 'back'>(null);
    const [isRenderOk, setIsRenderOk, getIsRenderOk] = useGetState(false);
    const allHighlightNodeInfo = useRef<Map<string, 'highlight' | 'animation'>>(new Map(null));
    const [currentRFAnimation, setCurrentRFAnimation] = useState<RFAnimationTpye>(null);
    const [shockHitedTargetId, setShockHitedTargetId] = useState(null);
    const currentRFAnimationRef = useRef(null);
    const delayDrawTimer = useRef<NodeJS.Timeout[]>([]);
    const currentIsDrawedMainBg = useRef(false);
    const onlyRenderSolutionBgRef = useLatest(onlyRenderSolutionMainBg);
    const currentIsDrawedMainBgWithPoint = useRef(false);
    const [currentActivePointCoord, setCurrentActivePointCoords] = useState({
      isLeft: false,
      coord: [],
      info: {} as Massage.DynamicActionConfig[0],
    });
    const [currentMoxActiveIdx, setCurrentMoxActiveIdx] = useState(null);
    const currentMoxActiveIdxRef = useRef(null);
    const { allValues, massageState } = useContext(MassageControlValueContext);
    const [leftLoopTips, setLeftLoopTips] = useState({
      times: 0,
      left: 0,
      top: 0,
    });
    const [rightLoopTips, setRigthLoopTips] = useState({
      times: 0,
      left: 0,
      top: 0,
    });
    const currentRenderMoxInfo = useRef<MatrixMoxibustionArea[]>([]);
    const isRenderedCustomLine = useRef(false);
    const isRenderedPreSetLine = useRef(false);

    /** 当前是自助方案 */
    const currentIsBuffetPlan = useMemo(
      () => currentServingOrder?.productName?.includes('自助'),
      [currentServingOrder?.productName]
    );

    const currentBuffetTip = useMemo(() => {
      if (!currentIsBuffetPlan) return;
      return BUFFET_PLAN_STEP_2_TIPS[allValues['robot.step'] as number];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allValues?.['robot.step'], currentIsBuffetPlan]);

    // 当前按摩区域功效文本
    const effectDesc = useMemo(() => {
      const activeTarget = currentActivePointCoord?.info?.target;

      // 找出穴位点的功效文本
      if (['ACUPOINT', 'NODULE'].includes(activeTarget?.type)) {
        return productInfo?.effectDesc?.find(
          item =>
            // ->代表是走线的功效说明
            !item.name?.includes('->') && item.name?.includes((activeTarget as any)?.acupointName?.replace(/左|右/, ''))
        )?.intro;
      } else if (activeTarget?.type === 'ACUPOINT_ROUTE') {
        const formatName = `${activeTarget?.starting?.acupointName} -> ${activeTarget?.ending?.acupointName}`;
        return productInfo?.effectDesc?.find(item => item.name === formatName)?.intro;
      } else if (activeTarget?.type === 'MATRIX_MOXIBUSTION_AREA') {
        return productInfo?.effectDesc?.find(item => currentActivePointCoord?.info?.actionName?.includes(item.name))
          ?.intro;
      }
    }, [currentActivePointCoord, productInfo]);

    const init = useCallback(async (gender: Customer.Gender) => {
      hasInitViewer.current = false;
      viewerRef.current = new Canvas2DViewer({
        containerId: 'model-2d-viewer',
        containerWidth: containerRef.current?.offsetWidth,
        containerHeight: containerRef.current?.offsetHeight,
        imgUrl: IAMGE_URL_BACK[gender || 'Female'],
        pointArr: [],
        onImageLoaded: () => {},
      });
      currentBodyImageRef.current = null;
      hasInitViewer.current = true;
    }, []);

    const formatBodyNodeListHandler = useCallback((nodeList: Palpation.PointTypeWithDirection[]) => {
      // 格式化y轴，使得在模型内可展示
      return nodeList?.map(item => ({
        ...item,
        position: {
          x: item?.position.x < 0 ? 0.02 : item?.position.x > 1 ? 0.98 : item?.position.x,
          y: Math.min(item?.position?.y || 0, 0.99),
        },
      }));
    }, []);

    const renderBodyPoints = useCallback(
      (bodyNodeList: Palpation.PointTypeWithDirection[]) => {
        if (bodyNodeList?.length) {
          viewerRef.current?.updateModelPoints(formatBodyNodeListHandler(bodyNodeList));
        }
        onRenderPointOK?.();
      },
      [formatBodyNodeListHandler, onRenderPointOK]
    );

    // 处理按摩自定义痛点坐标映射
    const formatMassageNoduleCoord = useCallback((info: Palpation.PointTypeWithDirection) => {
      if (!viewerRef.current) return;

      const backup = klona(info);
      backup.position = {
        x: info?.position.x < 0 ? 0.02 : info?.position.x > 1 ? 0.98 : info?.position.x,
        y: Math.min(info?.position?.y || 0, 0.99),
      };

      backup.pixelPosition = {
        x:
          (viewerRef.current?.modelBoundary[1][0] - viewerRef.current?.modelBoundary[0][0]) * backup.position.x +
          viewerRef.current?.modelBoundary[0][0],
        y:
          (viewerRef.current?.modelBoundary[1][1] - viewerRef.current?.modelBoundary[0][1]) * backup.position.y +
          viewerRef.current?.modelBoundary[0][1],
      };
      return viewerRef.current?.transformPoints([backup?.pixelPosition?.x, backup?.pixelPosition?.y]);
    }, []);

    /** 渲染磁震波预设路线 */
    const renderPreSetSplineHitInfo = useCallback(
      (
        coords: Array<{
          lineId: number;
          state: 'RUNNING' | 'PENDING' | 'DONE';
        }>
      ) => {
        if (isRenderedPreSetLine.current || !coords?.length) return;
        if (!viewerRef.current) return;

        coords?.forEach(item => {
          if (item.state === 'DONE') {
            viewerRef.current?.highlightDottedCircleLineInModel(item.lineId, { changeToSolidLine: true });
          } else if (item.state === 'RUNNING') {
            setShockHitedTargetId(item.lineId);
            viewerRef.current?.activeDottedCircleLineInModel(item.lineId);
          }
        });
        isRenderedPreSetLine.current = true;
      },
      []
    );

    /** 动态设置矩阵式艾灸区域 */
    const setMatrixMoxibustionArea = useCallback(
      async (areaList: MatrixMoxibustionArea[], config?: { isAbdomen: boolean }) => {
        if (!viewerRef.current) return;
        if (!currentServingOrder?.customerGender) return;

        if (!areaList?.length) {
          viewerRef.current?.clearDrawedImage();
          return;
        }

        const { isAbdomen = false } = config || {};
        const BODY_PART = isAbdomen ? 'front' : 'back';
        const MOX_CONFIG_LIST = isAbdomen ? allAbdomenMoxInfo : ModelSolution['moxibustion'];

        if (currentBodyImageRef.current !== BODY_PART) {
          currentBodyImageRef.current = BODY_PART;
          await viewerRef.current?.changeImageLayer(
            isAbdomen
              ? IAMGE_URL_FRONT[currentServingOrder?.customerGender]
              : IAMGE_URL_BACK[currentServingOrder?.customerGender]
          );
        }

        // 根据name去重
        const dedupList = areaList.filter((item, index, self) => index === self.findIndex(t => t.name === item.name));

        if (isEqual(dedupList, currentRenderMoxInfo.current)) {
          console.log('两次渲染效果一致，忽略渲染');
          return;
        }

        // 初始化渲染
        if (!currentRenderMoxInfo.current?.length) {
          for (const areaItem of dedupList) {
            const drawInfo = MOX_CONFIG_LIST.find(item => item.name === areaItem.name);
            if (!drawInfo) continue;
            await viewerRef.current?.drawImage(drawInfo.coord as number[], drawInfo.imageConfig, {
              opacity: areaItem?.opacity || 1,
              elementId: `mox-${areaItem.name}`,
              isMainBg: true,
            });
          }
        } else {
          // 增量值
          const addedList = differenceWith(dedupList, currentRenderMoxInfo.current, isEqual);
          // 减量值
          const deletedList = differenceWith(currentRenderMoxInfo.current, dedupList, isEqual);

          // 删除减少的区域
          if (deletedList?.length) {
            for (const item of deletedList) {
              await viewerRef.current?.clearDrawedImageByElementId(`mox-${item.name}`);
            }
          }

          // 渲染增加的区域
          if (addedList?.length) {
            for (const areaItem of addedList) {
              const drawInfo = MOX_CONFIG_LIST.find(item => item.name === areaItem.name);
              if (!drawInfo) continue;

              await viewerRef.current?.drawImage(drawInfo.coord as number[], drawInfo.imageConfig, {
                opacity: areaItem?.opacity || 1,
                elementId: `mox-${areaItem.name}`,
                isMainBg: true,
              });
            }
          }
        }

        currentRenderMoxInfo.current = dedupList;
      },
      [currentServingOrder?.customerGender]
    );

    /************* 以下为动态渲染方案 ************/

    /** 绘制动态按摩点效果 */
    const drawMassageActivePoint = useCallback((coord: number[]) => {
      if (!viewerRef.current) return;
      viewerRef.current?.drawImage(
        coord,
        { url: MassagePointActiveImg, size: [120, 120] },
        { animation: 'rotate-right' }
      );
      viewerRef.current?.drawImage(coord, { url: MassagePointActiveImg, size: [80, 80] }, { animation: 'rotate-left' });
    }, []);

    // 渲染按摩类型方案的手诊痛点
    const handleRenderMassageNodule = useCallback(
      (config: Massage.DynamicActionConfig) => {
        if (!viewerRef.current) return;

        if (!bodyNodeList?.length) {
          return;
        }

        const allNoduleAction = config.filter(item => item?.target?.type === 'NODULE');

        let currentActiveNoduleId = null;
        const hitedNoduleList: typeof allNoduleAction = [];

        allNoduleAction.forEach(item => {
          if (item.state === 'RUNNING' || item.state === 'INTENSIFYING') {
            currentActiveNoduleId = (item.target as any)?.noduleId;

            const currentNoduleInfo = bodyNodeList.find(item2 => (item.target as any)?.noduleId === item2.noduleId);

            // 自定义按摩点的坐标映射
            setTimeout(() => {
              // 设置label信息
              setCurrentActivePointCoords({
                coord: formatMassageNoduleCoord(currentNoduleInfo),
                isLeft: currentNoduleInfo?.isLeft,
                info: {
                  actionName: '痛点',
                  target: {
                    type: 'NODULE',
                    acupointName: '痛点',
                  } as any,
                } as any,
              });
            }, 100);
          }
          if (item.state === 'DONE') {
            hitedNoduleList.push(item);
          }
        });
        const handleRun = () => {
          // 清除之前的图层
          viewerRef.current?.clearAllMassageNodule();
          // 先渲染非当前打击痛点，最后渲染该痛点
          viewerRef.current?.updateMassageNodulePoints(formatBodyNodeListHandler(bodyNodeList), {
            shouldMoveToTop: true,
          });

          // 动态渲染已完成/正在打击的结节
          setTimeout(() => {
            hitedNoduleList.forEach(item =>
              viewerRef.current?.highlightMassageNodePoint((item.target as any)?.noduleId)
            );
            currentActiveNoduleId && viewerRef.current?.activeNodePoint(currentActiveNoduleId);
          }, 300);
        };

        if (currentActiveNoduleId || hitedNoduleList?.length) {
          if (allNoduleAction[0]?.state === 'DONE') {
            handleRun();
          } else {
            setTimeout(() => handleRun(), 600);
          }
        }
      },
      [bodyNodeList, formatBodyNodeListHandler, formatMassageNoduleCoord]
    );

    // 动态渲染方案
    const drawDynamicSolution = useCallback(
      async (solution: Massage.DynamicSolution, config: Massage.DynamicActionConfig) => {
        if (!viewerRef.current) return;
        if (shoulderHaveDynamicActionsPlan.includes(solution) && !config?.length) return;

        console.log('dynamicSolution', solution);

        // 清除之前的延迟渲染任务
        if (delayDrawTimer.current?.length) {
          delayDrawTimer.current?.forEach(timer => clearTimeout(timer));
          delayDrawTimer.current = [];
        }
        viewerRef.current?.clearDrawLayerExceptMainBg();

        // DONE
        if (solution === 'massage-yzc-18-dynamic') {
          const currentRunningItem = config?.find(item => item.state === 'RUNNING' || item.state === 'INTENSIFYING');

          // 当前在打击手诊按摩点,渲染手诊痛点,同时渲染主背景
          if (currentRunningItem?.target?.type === 'NODULE') {
            // 渲染主视图
            if (!currentIsDrawedMainBgWithPoint.current) {
              setLeftLoopTips(null);
              setRigthLoopTips(null);
              setCurrentActivePointCoords(null);

              viewerRef.current?.clearDrawLayer();

              await viewerRef.current?.drawImage(FullMassageBgConfig.left.coord, FullMassageBgConfig.left.imageConfig, {
                isMainBg: true,
              });
              await viewerRef.current?.drawImage(
                FullMassageBgConfig.right.coord,
                FullMassageBgConfig.right.imageConfig,
                { isMainBg: true }
              );

              FullMassagePointConfig.forEach(item => {
                viewerRef.current?.drawImage(
                  item.coord,
                  { url: MassagePointImg, size: MassagePointSize },
                  { isMainBg: true }
                );
              });

              currentIsDrawedMainBgWithPoint.current = true;
            }

            handleRenderMassageNodule(config);
            return;
          }

          let currentIsLeft = true;

          if (config?.[9].state === 'RUNNING') {
            viewerRef.current?.clearDrawLayer();
            currentIsDrawedMainBg.current = false;
          }

          // 代表当前在打击左侧
          if (config?.[9].state === 'PENDING') {
            currentIsLeft = true;

            if (!currentIsDrawedMainBg.current) {
              await viewerRef.current?.drawImage(FullMassageBgConfig.left.coord, FullMassageBgConfig.left.imageConfig, {
                isMainBg: true,
              });
              await viewerRef.current?.drawImage(
                FullMassageBgConfig.right.coord,
                FullMassageBgConfig.right.imageConfig,
                {
                  opacity: 0.4,
                  isMainBg: true,
                }
              );
              currentIsDrawedMainBg.current = true;
            }

            delayDrawTimer.current.push(
              setTimeout(
                () =>
                  viewerRef.current?.drawAnimationArrow({
                    coord: AnimationArrowConfig.full.left.coord,
                    offsetY: AnimationArrowConfig.full.left.offsetY,
                  }),
                500
              )
            );
          } else {
            currentIsLeft = false;
            if (!currentIsDrawedMainBg.current) {
              await viewerRef.current?.drawImage(FullMassageBgConfig.left.coord, FullMassageBgConfig.left.imageConfig, {
                isMainBg: true,
              });
              await viewerRef.current?.drawImage(
                FullMassageBgConfig.right.coord,
                FullMassageBgConfig.right.imageConfig,
                {
                  isMainBg: true,
                }
              );
              currentIsDrawedMainBg.current = true;
            }

            delayDrawTimer.current.push(
              setTimeout(
                () =>
                  viewerRef.current?.drawAnimationArrow({
                    coord: AnimationArrowConfig.full.right.coord,
                    offsetY: AnimationArrowConfig.full.right.offsetY,
                  }),
                500
              )
            );
          }

          FullMassagePointConfig.forEach((item, index) => {
            viewerRef.current?.drawImage(
              item.coord,
              { url: MassagePointImg, size: MassagePointSize },
              { opacity: config[index]?.state !== 'PENDING' ? 1 : 0.3 }
            );
            if (config[index].state === 'RUNNING' || config[index].state === 'INTENSIFYING') {
              setCurrentActivePointCoords({
                coord: viewerRef.current?.transformPoints(item.coord),
                isLeft: currentIsLeft,
                info: config[index],
              });
              delayDrawTimer.current.push(
                setTimeout(
                  () => drawMassageActivePoint(item.coord.map(item => item + MassageActivePointOffetSize)),
                  500
                )
              );
            }
          });
        }

        // DONE
        if (solution === 'massage-yzc-16-dynamic') {
          const currentRunningItem = config?.find(item => item.state === 'RUNNING' || item.state === 'INTENSIFYING');

          // 当前在打击手诊按摩点,渲染手诊痛点,同时渲染主背景
          if (currentRunningItem?.target?.type === 'NODULE') {
            // 渲染主视图
            if (!currentIsDrawedMainBgWithPoint.current) {
              setLeftLoopTips(null);
              setRigthLoopTips(null);
              setCurrentActivePointCoords(null);

              viewerRef.current?.clearDrawLayer();

              await viewerRef.current?.drawImage(
                FullMassageBgConfigWith16Point.left.coord,
                FullMassageBgConfigWith16Point.left.imageConfig,
                { isMainBg: true }
              );
              await viewerRef.current?.drawImage(
                FullMassageBgConfigWith16Point.right.coord,
                FullMassageBgConfigWith16Point.right.imageConfig,
                { isMainBg: true }
              );

              FullMassageWith16PointConfig.forEach(item => {
                viewerRef.current?.drawImage(
                  item.coord,
                  { url: MassagePointImg, size: MassagePointSize },
                  { isMainBg: true }
                );
              });

              currentIsDrawedMainBgWithPoint.current = true;
            }

            handleRenderMassageNodule(config);
            return;
          }

          let current16IsLeft = false;

          if (config?.[8].state === 'RUNNING') {
            viewerRef.current?.clearDrawLayer();
            currentIsDrawedMainBg.current = false;
          }

          // 代表当前在打击左侧
          if (config[8].state === 'PENDING') {
            current16IsLeft = true;

            if (!currentIsDrawedMainBg.current) {
              await viewerRef.current?.drawImage(
                FullMassageBgConfigWith16Point.left.coord,
                FullMassageBgConfigWith16Point.left.imageConfig,
                { isMainBg: true }
              );
              await viewerRef.current?.drawImage(
                FullMassageBgConfigWith16Point.right.coord,
                FullMassageBgConfigWith16Point.right.imageConfig,
                {
                  opacity: 0.4,
                  isMainBg: true,
                }
              );
              currentIsDrawedMainBg.current = true;
            }

            delayDrawTimer.current.push(
              setTimeout(
                () =>
                  viewerRef.current?.drawAnimationArrow({
                    coord: AnimationArrowConfig.massageWith16Points.left.coord,
                    offsetY: AnimationArrowConfig.massageWith16Points.left.offsetY,
                  }),
                500
              )
            );
          } else {
            current16IsLeft = false;

            if (!currentIsDrawedMainBg.current) {
              await viewerRef.current?.drawImage(
                FullMassageBgConfigWith16Point.left.coord,
                FullMassageBgConfigWith16Point.left.imageConfig,
                { isMainBg: true }
              );
              await viewerRef.current?.drawImage(
                FullMassageBgConfigWith16Point.right.coord,
                FullMassageBgConfigWith16Point.right.imageConfig,
                { isMainBg: true }
              );
              currentIsDrawedMainBg.current = true;
            }

            delayDrawTimer.current.push(
              setTimeout(
                () =>
                  viewerRef.current?.drawAnimationArrow({
                    coord: AnimationArrowConfig.massageWith16Points.right.coord,
                    offsetY: AnimationArrowConfig.massageWith16Points.right.offsetY,
                  }),
                500
              )
            );
          }

          FullMassageWith16PointConfig.forEach((item, index) => {
            viewerRef.current?.drawImage(
              item.coord,
              { url: MassagePointImg, size: MassagePointSize },
              { opacity: config[index]?.state !== 'PENDING' ? 1 : 0.3 }
            );
            if (config[index].state === 'RUNNING' || config[index].state === 'INTENSIFYING') {
              delayDrawTimer.current.push(
                setTimeout(
                  () => drawMassageActivePoint(item.coord.map(item => item + MassageActivePointOffetSize)),
                  500
                )
              );

              setCurrentActivePointCoords({
                coord: viewerRef.current?.transformPoints(item.coord),
                isLeft: current16IsLeft,
                info: config[index],
              });
            }
          });
        }
        // 一指禅展会版本10点位动态图
        if (solution === 'massage-yzc-10-dynamic') {
          const currentRunningItem = config?.find(item => item.state === 'RUNNING' || item.state === 'INTENSIFYING');
          // 当前在打击手诊按摩点,渲染手诊痛点,同时渲染主背景
          if (currentRunningItem?.target?.type === 'NODULE') {
            // 渲染主视图
            if (!currentIsDrawedMainBgWithPoint.current) {
              setLeftLoopTips(null);
              setRigthLoopTips(null);
              setCurrentActivePointCoords(null);

              viewerRef.current?.clearDrawLayer();

              await viewerRef.current?.drawImage(FullMassageBgConfig.left.coord, FullMassageBgConfig.left.imageConfig, {
                isMainBg: true,
              });
              await viewerRef.current?.drawImage(
                FullMassageBgConfig.right.coord,
                FullMassageBgConfig.right.imageConfig,
                { isMainBg: true }
              );

              FullMassagePointConfigWith10Point.forEach(item => {
                viewerRef.current?.drawImage(
                  item.coord,
                  { url: MassagePointImg, size: MassagePointSize },
                  { isMainBg: true }
                );
              });

              currentIsDrawedMainBgWithPoint.current = true;
            }

            handleRenderMassageNodule(config);
            return;
          }

          let currentIsLeft = true;

          if (config?.[5].state === 'RUNNING') {
            viewerRef.current?.clearDrawLayer();
            currentIsDrawedMainBg.current = false;
          }

          // 代表当前在打击左侧
          if (config?.[5].state === 'PENDING') {
            currentIsLeft = true;

            if (!currentIsDrawedMainBg.current) {
              await viewerRef.current?.drawImage(FullMassageBgConfig.left.coord, FullMassageBgConfig.left.imageConfig, {
                isMainBg: true,
              });
              await viewerRef.current?.drawImage(
                FullMassageBgConfig.right.coord,
                FullMassageBgConfig.right.imageConfig,
                {
                  opacity: 0.4,
                  isMainBg: true,
                }
              );
              currentIsDrawedMainBg.current = true;
            }

            delayDrawTimer.current.push(
              setTimeout(
                () =>
                  viewerRef.current?.drawAnimationArrow({
                    coord: AnimationArrowConfig.full.left.coord,
                    offsetY: AnimationArrowConfig.full.left.offsetY,
                  }),
                500
              )
            );
          } else {
            currentIsLeft = false;
            if (!currentIsDrawedMainBg.current) {
              await viewerRef.current?.drawImage(FullMassageBgConfig.left.coord, FullMassageBgConfig.left.imageConfig, {
                isMainBg: true,
              });
              await viewerRef.current?.drawImage(
                FullMassageBgConfig.right.coord,
                FullMassageBgConfig.right.imageConfig,
                {
                  isMainBg: true,
                }
              );
              currentIsDrawedMainBg.current = true;
            }

            delayDrawTimer.current.push(
              setTimeout(
                () =>
                  viewerRef.current?.drawAnimationArrow({
                    coord: AnimationArrowConfig.full.right.coord,
                    offsetY: AnimationArrowConfig.full.right.offsetY,
                  }),
                500
              )
            );
          }

          FullMassagePointConfigWith10Point.forEach((item, index) => {
            viewerRef.current?.drawImage(
              item.coord,
              { url: MassagePointImg, size: MassagePointSize },
              { opacity: config[index]?.state !== 'PENDING' ? 1 : 0.3 }
            );
            if (config[index].state === 'RUNNING' || config[index].state === 'INTENSIFYING') {
              setCurrentActivePointCoords({
                coord: viewerRef.current?.transformPoints(item.coord),
                isLeft: currentIsLeft,
                info: config[index],
              });
              delayDrawTimer.current.push(
                setTimeout(
                  () => drawMassageActivePoint(item.coord.map(item => item + MassageActivePointOffetSize)),
                  500
                )
              );
            }
          });
        }

        // 吕教授手法肩部按摩V2版本
        if (solution === 'massage-shoulder-lv-dynamic') {
          let currentRunningItemIdx = -1;
          const currentRunningItem = config.find((item, index) => {
            if (item.state === 'RUNNING' || item.state === 'INTENSIFYING') {
              currentRunningItemIdx = index;
              return true;
            }
          });

          // 当前在打击手诊按摩点,渲染手诊痛点,同时渲染主背景
          if (currentRunningItem?.target?.type === 'NODULE') {
            // 渲染主视图
            if (!currentIsDrawedMainBgWithPoint.current) {
              setLeftLoopTips(null);
              setRigthLoopTips(null);
              setCurrentActivePointCoords(null);

              viewerRef.current?.clearDrawLayer();

              await viewerRef.current?.drawImage(
                ShouldeLvMassageBgConfigV2.left.coord,
                ShouldeLvMassageBgConfigV2.left.imageConfig,
                { isMainBg: true }
              );
              await viewerRef.current?.drawImage(
                ShouldeLvMassageBgConfigV2.right.coord,
                ShouldeLvMassageBgConfigV2.right.imageConfig,
                { isMainBg: true }
              );
              ShoulderLvMassagePointConfigV2.forEach((item: { coord: any[] }) => {
                viewerRef.current?.drawImage(
                  item.coord,
                  { url: MassagePointImg, size: MassagePointSize },
                  { isMainBg: true }
                );
              });

              currentIsDrawedMainBgWithPoint.current = true;
            }

            handleRenderMassageNodule(config);
            return;
          }

          // 渲染主视图
          if (!currentIsDrawedMainBg.current) {
            setLeftLoopTips(null);
            setRigthLoopTips(null);
            setCurrentActivePointCoords(null);

            viewerRef.current?.clearDrawLayer();

            await viewerRef.current?.drawImage(
              ShouldeLvMassageBgConfigV2.left.coord,
              ShouldeLvMassageBgConfigV2.left.imageConfig,
              { isMainBg: true }
            );

            await viewerRef.current?.drawImage(
              ShouldeLvMassageBgConfigV2.right.coord,
              ShouldeLvMassageBgConfigV2.right.imageConfig,
              { isMainBg: true, opacity: currentRunningItemIdx < 8 ? 0.4 : 1 }
            );

            currentIsDrawedMainBg.current = true;
          }

          if (currentRunningItemIdx === 8) {
            viewerRef.current?.clearDrawLayer();

            await viewerRef.current?.drawImage(
              ShouldeLvMassageBgConfigV2.left.coord,
              ShouldeLvMassageBgConfigV2.left.imageConfig,
              { isMainBg: true }
            );

            await viewerRef.current?.drawImage(
              ShouldeLvMassageBgConfigV2.right.coord,
              ShouldeLvMassageBgConfigV2.right.imageConfig,
              { isMainBg: true, opacity: 1 }
            );
            currentIsDrawedMainBg.current = true;
          }

          ShoulderLvMassagePointConfigV2.forEach((item: { coord: any[] }, index) => {
            viewerRef.current?.drawImage(
              item.coord,
              {
                url: MassagePointImg,
                size: MassagePointSize,
              },
              { opacity: currentRunningItemIdx < index ? 0.4 : 1 }
            );

            if (currentRunningItemIdx % 16 === index) {
              setCurrentActivePointCoords({
                coord: viewerRef.current?.transformPoints(item.coord),
                isLeft: currentRunningItem?.actionName?.includes('左'),
                info: config[index],
              });
              delayDrawTimer.current.push(
                setTimeout(
                  () => drawMassageActivePoint(item.coord.map(item => item + MassageActivePointOffetSize)),
                  700
                )
              );
            }
          });

          if (currentRunningItem?.actionName?.includes('左')) {
            await viewerRef.current?.drawAnimationArrow({
              coord: AnimationArrowConfig.lvShoulderV2.left.coord,
              offsetY: AnimationArrowConfig.lvShoulderV2.left.offsetY,
            });
          } else {
            await viewerRef.current?.drawAnimationArrow({
              coord: AnimationArrowConfig.lvShoulderV2.right.coord,
              offsetY: AnimationArrowConfig.lvShoulderV2.right.offsetY,
            });
          }

          if (currentRunningItemIdx > 7 && currentRunningItemIdx <= 15) {
            setLeftLoopTips({
              top: 395,
              left: 240,
              times: 1,
            });
            setRigthLoopTips({
              top: 395,
              left: 307,
              times: 0,
            });
          } else if (currentRunningItemIdx > 15 && currentRunningItemIdx <= 23) {
            setLeftLoopTips({
              top: 395,
              left: 240,
              times: 1,
            });
            setRigthLoopTips({
              top: 395,
              left: 307,
              times: 1,
            });
          } else if (currentRunningItemIdx > 23 && currentRunningItemIdx <= 31) {
            setLeftLoopTips({
              top: 395,
              left: 240,
              times: 2,
            });
            setRigthLoopTips({
              top: 395,
              left: 307,
              times: 1,
            });
          } else if (currentRunningItemIdx > 31 && currentRunningItemIdx <= 39) {
            setLeftLoopTips({
              top: 395,
              left: 240,
              times: 2,
            });
            setRigthLoopTips({
              top: 395,
              left: 307,
              times: 2,
            });
          } else if (currentRunningItemIdx > 39 && currentRunningItemIdx <= 47) {
            setLeftLoopTips({
              top: 395,
              left: 240,
              times: 3,
            });
            setRigthLoopTips({
              top: 395,
              left: 307,
              times: 2,
            });
          }
        }

        // 吕教授手法肩部按摩V2优化版本
        if (solution === 'massage-shoulder-lv-v2-dynamic') {
          let currentRunningItemIdx = -1;
          const currentRunningItem = config.find((item, index) => {
            if (item.state === 'RUNNING' || item.state === 'INTENSIFYING') {
              currentRunningItemIdx = index;
              return true;
            }
          });

          // 当前在打击手诊按摩点,渲染手诊痛点,同时渲染主背景
          if (currentRunningItem?.target?.type === 'NODULE') {
            // 渲染主视图
            if (!currentIsDrawedMainBgWithPoint.current) {
              setLeftLoopTips(null);
              setRigthLoopTips(null);
              setCurrentActivePointCoords(null);

              viewerRef.current?.clearDrawLayer();

              await viewerRef.current?.drawImage(
                ShouldeLvMassageBgConfigV2Optimize.left.coord,
                ShouldeLvMassageBgConfigV2Optimize.left.imageConfig,
                { isMainBg: true }
              );
              await viewerRef.current?.drawImage(
                ShouldeLvMassageBgConfigV2Optimize.right.coord,
                ShouldeLvMassageBgConfigV2Optimize.right.imageConfig,
                { isMainBg: true }
              );
              ShoulderLvMassagePointConfigV2Optimize.forEach((item: { coord: any[] }) => {
                viewerRef.current?.drawImage(
                  item.coord,
                  { url: MassagePointImg, size: MassagePointSize },
                  { isMainBg: true }
                );
              });

              currentIsDrawedMainBgWithPoint.current = true;
            }

            handleRenderMassageNodule(config);
            return;
          }

          // 渲染主视图
          if (!currentIsDrawedMainBg.current) {
            setLeftLoopTips(null);
            setRigthLoopTips(null);
            setCurrentActivePointCoords(null);

            viewerRef.current?.clearDrawLayer();

            await viewerRef.current?.drawImage(
              ShouldeLvMassageBgConfigV2Optimize.left.coord,
              ShouldeLvMassageBgConfigV2Optimize.left.imageConfig,
              { isMainBg: true }
            );

            await viewerRef.current?.drawImage(
              ShouldeLvMassageBgConfigV2Optimize.right.coord,
              ShouldeLvMassageBgConfigV2Optimize.right.imageConfig,
              { isMainBg: true, opacity: currentRunningItemIdx < 5 ? 0.4 : 1 }
            );

            currentIsDrawedMainBg.current = true;
          }

          if (currentRunningItemIdx === 4) {
            viewerRef.current?.clearDrawLayer();

            await viewerRef.current?.drawImage(
              ShouldeLvMassageBgConfigV2Optimize.left.coord,
              ShouldeLvMassageBgConfigV2Optimize.left.imageConfig,
              { isMainBg: true }
            );

            await viewerRef.current?.drawImage(
              ShouldeLvMassageBgConfigV2Optimize.right.coord,
              ShouldeLvMassageBgConfigV2Optimize.right.imageConfig,
              { isMainBg: true, opacity: 1 }
            );
            currentIsDrawedMainBg.current = true;
          }

          // 在打点
          if (currentRunningItemIdx <= 7) {
            ShoulderLvMassagePointConfigV2Optimize.forEach((item: { coord: any[] }, index) => {
              viewerRef.current?.drawImage(
                item.coord,
                {
                  url: MassagePointImg,
                  size: MassagePointSize,
                },
                { opacity: currentRunningItemIdx < index ? 0.4 : 1 }
              );

              if (currentRunningItemIdx === index) {
                setCurrentActivePointCoords({
                  coord: viewerRef.current?.transformPoints(item.coord),
                  isLeft: currentRunningItem?.actionName?.includes('左'),
                  info: config[index],
                });
                delayDrawTimer.current.push(
                  setTimeout(
                    () => drawMassageActivePoint(item.coord.map(item => item + MassageActivePointOffetSize)),
                    700
                  )
                );
              }
            });
          } else {
            // 在走线
            const currentIsLeft = currentRunningItemIdx % 2 === 0;
            setCurrentActivePointCoords({
              coord: currentIsLeft ? [235, 225] : [320, 225],
              isLeft: currentIsLeft,
              info: config[currentRunningItemIdx] as any,
            });
            ShoulderLvMassagePointConfigV2OptimizeRoute.forEach((item: { coord: any[] }) => {
              viewerRef.current?.drawImage(
                item.coord,
                {
                  url: MassagePointImg,
                  size: MassagePointSize,
                },
                { opacity: 1 }
              );
            });

            // 绘制走线流程的的虚线
            viewerRef.current?.drawDottedLine(
              transfromPointCoord([
                ...ShoulderLvMassagePointConfigV2OptimizeRoute[0].coord,
                ...ShoulderLvMassagePointConfigV2OptimizeRoute[1].coord,
              ]),
              DASH_LINE_CONFIG
            );
            viewerRef.current?.drawDottedLine(
              transfromPointCoord([
                ...ShoulderLvMassagePointConfigV2OptimizeRoute[2].coord,
                ...ShoulderLvMassagePointConfigV2OptimizeRoute[3].coord,
              ]),
              currentRunningItemIdx === 8 ? DASH_LINE_OPACITY_CONFIG : DASH_LINE_CONFIG
            );

            const currentAction2IdConfig = lvShoulderMassageV2ActionId2Times.find(
              item => item.actionId === currentRunningItem?.actionId
            );

            setLeftLoopTips({
              top: 270,
              left: 220,
              times: currentAction2IdConfig?.leftTimes || 0,
            });
            setRigthLoopTips({
              top: 270,
              left: 323,
              times: currentAction2IdConfig?.rightTimes || 0,
            });
          }

          if (currentRunningItem?.actionName?.includes('左')) {
            await viewerRef.current?.drawAnimationArrow({
              coord: AnimationArrowConfig.lvShoulderV2Optimize.left.coord,
              offsetY: AnimationArrowConfig.lvShoulderV2Optimize.left.offsetY,
            });
          } else {
            await viewerRef.current?.drawAnimationArrow({
              coord: AnimationArrowConfig.lvShoulderV2Optimize.right.coord,
              offsetY: AnimationArrowConfig.lvShoulderV2Optimize.right.offsetY,
            });
          }
        }

        // 吕教授手法腰部按摩
        if (solution === 'massage-waist-lv-dynamic') {
          let currentRunningItemIdx = -1;
          const currentRunningItem = config.find((item, index) => {
            if (item.state === 'RUNNING' || item.state === 'INTENSIFYING') {
              currentRunningItemIdx = index;
              return true;
            }
          });

          // 当前在打击手诊按摩点,渲染手诊痛点,同时渲染主背景
          if (currentRunningItem?.target?.type === 'NODULE') {
            // 渲染主视图
            if (!currentIsDrawedMainBgWithPoint.current) {
              setLeftLoopTips(null);
              setRigthLoopTips(null);
              setCurrentActivePointCoords(null);

              viewerRef.current?.clearDrawLayer();

              await viewerRef.current?.drawImage(WaistLvMassageBg.left.coord, WaistLvMassageBg.left.imageConfig, {
                isMainBg: true,
              });
              await viewerRef.current?.drawImage(WaistLvMassageBg.right.coord, WaistLvMassageBg.right.imageConfig, {
                isMainBg: true,
              });
              WaistLvMassagePointConfig.forEach((item: { coord: any[] }) => {
                viewerRef.current?.drawImage(
                  item.coord,
                  { url: MassagePointImg, size: MassagePointSize },
                  { isMainBg: true }
                );
              });

              currentIsDrawedMainBgWithPoint.current = true;
            }

            handleRenderMassageNodule(config);
            return;
          }

          // 渲染主视图
          if (!currentIsDrawedMainBg.current) {
            setLeftLoopTips(null);
            setRigthLoopTips(null);
            setCurrentActivePointCoords(null);

            viewerRef.current?.clearDrawLayer();

            await viewerRef.current?.drawImage(
              currentRunningItemIdx > 8 ? WaistLvMassageBgStep2.left.coord : WaistLvMassageBg.left.coord,
              currentRunningItemIdx > 8 ? WaistLvMassageBgStep2.left.imageConfig : WaistLvMassageBg.left.imageConfig,
              {
                isMainBg: true,
              }
            );

            await viewerRef.current?.drawImage(
              currentRunningItemIdx > 8 ? WaistLvMassageBgStep2.right.coord : WaistLvMassageBg.right.coord,
              currentRunningItemIdx > 8 ? WaistLvMassageBgStep2.right.imageConfig : WaistLvMassageBg.right.imageConfig,
              {
                isMainBg: true,
                opacity: currentRunningItemIdx < 4 ? 0.4 : 1,
              }
            );

            currentIsDrawedMainBg.current = true;
          }

          if (currentRunningItemIdx === 4) {
            viewerRef.current?.clearDrawLayer();

            await viewerRef.current?.drawImage(WaistLvMassageBg.left.coord, WaistLvMassageBg.left.imageConfig, {
              isMainBg: true,
            });

            await viewerRef.current?.drawImage(WaistLvMassageBg.right.coord, WaistLvMassageBg.right.imageConfig, {
              isMainBg: true,
              opacity: 1,
            });
            currentIsDrawedMainBg.current = true;
          }

          if (currentRunningItemIdx === 8) {
            viewerRef.current?.clearDrawLayer();

            await viewerRef.current?.drawImage(
              WaistLvMassageBgStep2.left.coord,
              WaistLvMassageBgStep2.left.imageConfig,
              {
                isMainBg: true,
              }
            );

            await viewerRef.current?.drawImage(
              WaistLvMassageBgStep2.right.coord,
              WaistLvMassageBgStep2.right.imageConfig,
              {
                isMainBg: true,
              }
            );
            currentIsDrawedMainBg.current = true;
          }

          if (currentRunningItem?.actionName?.includes('左')) {
            await viewerRef.current?.drawAnimationArrow({
              coord: AnimationArrowConfig.lvWaist.left.coord,
              offsetY: AnimationArrowConfig.lvWaist.left.offsetY,
            });
          } else {
            await viewerRef.current?.drawAnimationArrow({
              coord: AnimationArrowConfig.lvWaist.right.coord,
              offsetY: AnimationArrowConfig.lvWaist.right.offsetY,
            });
          }

          // 在打线
          if (currentRunningItemIdx >= 8) {
            const currentIsLeft = currentRunningItemIdx % 2 === 0;
            setCurrentActivePointCoords({
              coord: currentIsLeft ? [230, 420] : [320, 420],
              isLeft: currentIsLeft,
              info: config[currentRunningItemIdx] as any,
            });
            setLeftLoopTips({
              top: 460,
              left: 218,
              times: lvWaistMassageActionId2Times.find(item => item.actionId === currentRunningItem?.actionId)
                ?.leftTimes,
            });
            setRigthLoopTips({
              top: 460,
              left: 324,
              times: lvWaistMassageActionId2Times.find(item => item.actionId === currentRunningItem?.actionId)
                ?.rightTimes,
            });
            WaistLvMassagePointRouteConfig.forEach((item: { coord: any[] }) => {
              viewerRef.current?.drawImage(
                item.coord,
                {
                  url: MassagePointImg,
                  size: MassagePointSize,
                },
                { opacity: 1 }
              );
            });

            viewerRef.current?.drawDottedLine(
              transfromPointCoord([
                ...WaistLvMassagePointRouteConfig[0].coord,
                ...WaistLvMassagePointRouteConfig[1].coord,
              ]),
              DASH_LINE_CONFIG
            );
            viewerRef.current?.drawDottedLine(
              transfromPointCoord([
                ...WaistLvMassagePointRouteConfig[2].coord,
                ...WaistLvMassagePointRouteConfig[3].coord,
              ]),
              currentRunningItemIdx === 8 ? DASH_LINE_OPACITY_CONFIG : DASH_LINE_CONFIG
            );
          } else {
            // 在打点
            WaistLvMassagePointConfig.forEach((item: { coord: any[] }, index) => {
              viewerRef.current?.drawImage(
                item.coord,
                {
                  url: MassagePointImg,
                  size: MassagePointSize,
                },
                { opacity: currentRunningItemIdx < index ? 0.4 : 1 }
              );

              if (currentRunningItemIdx === index) {
                setCurrentActivePointCoords({
                  coord: viewerRef.current?.transformPoints(item.coord),
                  isLeft: currentRunningItem?.actionName?.includes('左'),
                  info: config[index],
                });
                delayDrawTimer.current.push(
                  setTimeout(
                    () => drawMassageActivePoint(item.coord.map(item => item + MassageActivePointOffetSize)),
                    700
                  )
                );
              }
            });
          }
        }

        // DONE
        if (solution === 'massage-yzc-zhai-dynamic') {
          let currentZhaiIsLeft = false;
          const currentRunningItemYzcZhai = config?.find(
            item => item.state === 'RUNNING' || item.state === 'INTENSIFYING'
          );
          const currentIsHitPoint = currentRunningItemYzcZhai?.target?.type === 'ACUPOINT';
          // 当前在打击手诊按摩点,渲染手诊痛点,同时渲染主背景
          if (currentRunningItemYzcZhai?.target?.type === 'NODULE') {
            // 渲染主视图
            if (!currentIsDrawedMainBgWithPoint.current) {
              setLeftLoopTips(null);
              setRigthLoopTips(null);
              setCurrentActivePointCoords(null);

              viewerRef.current?.clearDrawLayer();

              await viewerRef.current?.drawImage(
                FullMassageBgConfigWith16Point.left.coord,
                FullMassageBgConfigWith16Point.left.imageConfig,
                { isMainBg: true }
              );
              await viewerRef.current?.drawImage(
                FullMassageBgConfigWith16Point.right.coord,
                FullMassageBgConfigWith16Point.right.imageConfig,
                { isMainBg: true }
              );

              ZhaiTeacherRoutePoints.forEach(item => {
                viewerRef.current?.drawImage(
                  item.coord,
                  { url: MassagePointImg, size: MassagePointSize },
                  { opacity: 1, isMainBg: true }
                );
              });

              currentIsDrawedMainBgWithPoint.current = true;
            }

            handleRenderMassageNodule(config);
            return;
          }

          // 当前在打按摩点
          if (currentIsHitPoint) {
            if (config[8].state === 'RUNNING') {
              viewerRef.current?.clearDrawLayer();
              currentIsDrawedMainBg.current = false;
            }

            if (config[8].state === 'PENDING') {
              // 代表当前在打击左侧
              currentZhaiIsLeft = true;

              if (!currentIsDrawedMainBg.current) {
                await viewerRef.current?.drawImage(
                  MassageZhaiBgConfig.left.coord,
                  MassageZhaiBgConfig.left.imageConfig,
                  {
                    isMainBg: true,
                  }
                );
                await viewerRef.current?.drawImage(
                  MassageZhaiBgConfig.right.coord,
                  MassageZhaiBgConfig.right.imageConfig,
                  { opacity: 0.4, isMainBg: true }
                );
                currentIsDrawedMainBg.current = true;
              }

              await viewerRef.current?.drawAnimationArrow({
                coord: AnimationArrowConfig.zhaiTeacher.left.coord,
                offsetY: AnimationArrowConfig.zhaiTeacher.left.offsetY,
              });
            } else {
              currentZhaiIsLeft = false;

              if (!currentIsDrawedMainBg.current) {
                await viewerRef.current?.drawImage(
                  MassageZhaiBgConfig.left.coord,
                  MassageZhaiBgConfig.left.imageConfig,
                  {
                    isMainBg: true,
                  }
                );
                await viewerRef.current?.drawImage(
                  MassageZhaiBgConfig.right.coord,
                  MassageZhaiBgConfig.right.imageConfig,
                  { isMainBg: true }
                );
                currentIsDrawedMainBg.current = true;
              }

              await viewerRef.current?.drawAnimationArrow({
                coord: AnimationArrowConfig.zhaiTeacher.right.coord,
                offsetY: AnimationArrowConfig.zhaiTeacher.right.offsetY,
              });
            }

            MassageZhaiPointsConfig.forEach((item, index) => {
              viewerRef.current?.drawImage(
                item.coord,
                { url: MassagePointImg, size: MassagePointSize },
                { opacity: config[index]?.state !== 'PENDING' ? 1 : 0.3 }
              );
              if (config[index].state === 'RUNNING' || config[index].state === 'INTENSIFYING') {
                drawMassageActivePoint(item.coord.map(item => item + MassageActivePointOffetSize));
                setCurrentActivePointCoords({
                  coord: viewerRef.current?.transformPoints(item.coord),
                  isLeft: currentZhaiIsLeft,
                  info: config[index],
                });
              }
            });
          } else {
            const currentActionIdInfo = ZhaiActionId2Times.find(
              item => item.actionId === currentRunningItemYzcZhai?.actionId
            );

            setLeftLoopTips({
              top: 459,
              left: 240,
              times: currentActionIdInfo?.leftTimes,
            });
            setRigthLoopTips({
              top: 459,
              left: 307,
              times: currentActionIdInfo?.rightTimes,
            });

            if ([17, 20].includes(currentRunningItemYzcZhai?.actionId)) {
              viewerRef.current?.clearDrawLayer();
              currentIsDrawedMainBg.current = false;
            }

            // 当前在打线
            if ((currentRunningItemYzcZhai?.target as any)?.starting?.acupointName?.includes('左')) {
              if (!currentIsDrawedMainBg.current) {
                await viewerRef.current?.drawImage(
                  FullMassageBgConfigWith16Point.left.coord,
                  FullMassageBgConfigWith16Point.left.imageConfig,
                  { isMainBg: true }
                );
                await viewerRef.current?.drawImage(
                  FullMassageBgConfigWith16Point.right.coord,
                  FullMassageBgConfigWith16Point.right.imageConfig,
                  {
                    opacity: +currentRunningItemYzcZhai?.actionId < 20 ? 0.4 : 1,
                    isMainBg: true,
                  }
                );
                currentIsDrawedMainBg.current = true;
              }

              await viewerRef.current?.drawAnimationArrow({
                coord: AnimationArrowConfig.massageWith16Points.left.coord,
                offsetY: AnimationArrowConfig.massageWith16Points.left.offsetY,
              });

              setCurrentActivePointCoords({
                coord: [230, 320],
                isLeft: true,
                info: currentRunningItemYzcZhai,
              });
            }
            if ((currentRunningItemYzcZhai?.target as any)?.starting?.acupointName?.includes('右')) {
              if (!currentIsDrawedMainBg.current) {
                await viewerRef.current?.drawImage(
                  FullMassageBgConfigWith16Point.left.coord,
                  FullMassageBgConfigWith16Point.left.imageConfig,
                  { isMainBg: true }
                );
                await viewerRef.current?.drawImage(
                  FullMassageBgConfigWith16Point.right.coord,
                  FullMassageBgConfigWith16Point.right.imageConfig,
                  { isMainBg: true }
                );
                currentIsDrawedMainBg.current = true;
              }
              await viewerRef.current?.drawAnimationArrow({
                coord: AnimationArrowConfig.massageWith16Points.right.coord,
                offsetY: AnimationArrowConfig.massageWith16Points.right.offsetY,
              });

              setCurrentActivePointCoords({
                coord: [325, 320],
                isLeft: false,
                info: currentRunningItemYzcZhai,
              });
            }
            ZhaiTeacherRoutePoints.forEach((item, index) => {
              viewerRef.current?.drawImage(
                item.coord,
                { url: MassagePointImg, size: MassagePointSize },
                { opacity: index > 1 && +currentRunningItemYzcZhai?.actionId < 20 ? 0.4 : 1 }
              );
            });
            viewerRef.current?.drawDottedLine(
              transfromPointCoord([...ZhaiTeacherRoutePoints[0].coord, ...ZhaiTeacherRoutePoints[1].coord]),
              DASH_LINE_CONFIG
            );
            viewerRef.current?.drawDottedLine(
              transfromPointCoord([...ZhaiTeacherRoutePoints[2].coord, ...ZhaiTeacherRoutePoints[3].coord]),
              currentRunningItemYzcZhai?.actionId < 20 ? DASH_LINE_OPACITY_CONFIG : DASH_LINE_CONFIG
            );
          }
        }

        // DONE
        if (solution === 'waist-massage-yzc-dynamic') {
          const currentRunningItem = config?.find(item => item.state === 'RUNNING') as any;
          const currentRunningIndex = config?.findIndex(item => item.state === 'RUNNING');

          const currentActionIdInfo = WaistActionId2Times.find(item => item.actionId === currentRunningItem?.actionId);

          setLeftLoopTips({
            top: 513,
            left: 240,
            times: currentActionIdInfo?.leftTimes,
          });
          setRigthLoopTips({
            top: 513,
            left: 307,
            times: currentActionIdInfo?.rightTimes,
          });

          if (currentRunningItem?.target?.acupointName.includes('左')) {
            await viewerRef.current?.drawAnimationArrow({
              coord: AnimationArrowConfig.waist.left.coord,
              offsetY: AnimationArrowConfig.waist.left.offsetY,
            });

            setCurrentActivePointCoords({
              coord: [220, 440],
              isLeft: true,
              info: {
                target: {
                  type: 'ACUPOINT_ROUTE',
                  starting: { type: 'ACUPOINT', acupointName: '左三焦穴', acupointId: 'acupointId' },
                  ending: { type: 'ACUPOINT', acupointName: '左关元穴', acupointId: 'acupointId' },
                },
              } as any,
            });
          }

          if (currentRunningItem?.target?.acupointName.includes('右')) {
            await viewerRef.current?.drawAnimationArrow({
              coord: AnimationArrowConfig.waist.right.coord,
              offsetY: AnimationArrowConfig.waist.right.offsetY,
            });
            setCurrentActivePointCoords({
              coord: [310, 440],
              isLeft: false,
              info: {
                target: {
                  type: 'ACUPOINT_ROUTE',
                  starting: { type: 'ACUPOINT', acupointName: '右三焦穴', acupointId: 'acupointId' },
                  ending: { type: 'ACUPOINT', acupointName: '右关元穴', acupointId: 'acupointId' },
                },
              } as any,
            });
          }

          if (currentRunningIndex === 1) {
            viewerRef.current?.clearDrawLayer();
            currentIsDrawedMainBg.current = false;
          }

          if (!currentIsDrawedMainBg.current) {
            await viewerRef.current?.drawImage(WaistMassageBgConfig.left.coord, WaistMassageBgConfig.left.imageConfig, {
              isMainBg: true,
            });
            await viewerRef.current?.drawImage(
              WaistMassageBgConfig.right.coord,
              WaistMassageBgConfig.right.imageConfig,
              {
                opacity: currentRunningIndex === 0 ? 0.4 : 1,
                isMainBg: true,
              }
            );
            currentIsDrawedMainBg.current = true;
          }

          WaistMassagePointConfig.forEach((item, index) => {
            if (currentRunningIndex === 0 && [1, 3].includes(index)) {
              viewerRef.current?.drawImage(
                item.coord,
                { url: MassagePointImg, size: MassagePointSize },
                { opacity: 0.4 }
              );
            } else {
              viewerRef.current?.drawImage(
                item.coord,
                { url: MassagePointImg, size: MassagePointSize },
                { opacity: 1 }
              );
            }
          });
        }

        // DONE
        if (solution === 'waist-RF-dynamic') {
          for (const item of ModelSolution['waist-RF']) {
            switch (item?.type) {
              case 'spline':
                viewerRef.current?.drawArrowSpline(item.name, item.coord, item.groupCoord);
                break;
              case 'circle':
                viewerRef.current?.drawCircle(item.name, item.coord as [number, number], true);
                break;
              case 'image':
                await viewerRef.current?.drawImage(item.coord as number[], item.imageConfig);
                break;
            }
          }
          setTimeout(() => {
            currentRFAnimationRef.current = 'waist-rf';
            setCurrentRFAnimation(currentRFAnimationRef.current);
          }, 400);
        }

        // DONE
        if (solution === 'shoulder-RF-dynamic') {
          for (const item of ModelSolution['shoulder-RF']) {
            switch (item?.type) {
              case 'spline':
                viewerRef.current?.drawArrowSpline(item.name, item.coord, item.groupCoord);
                break;
              case 'circle':
                viewerRef.current?.drawCircle(item.name, item.coord as [number, number], true);
                break;
              case 'image':
                await viewerRef.current?.drawImage(item.coord as number[], item.imageConfig);
                break;
            }
          }
          setTimeout(() => {
            currentRFAnimationRef.current = 'shoulder-rf';
            setCurrentRFAnimation(currentRFAnimationRef.current);
          }, 400);
        }

        // DONE
        if (solution === 'neck-RF-dynamic') {
          for (const item of ModelSolution['neck-RF']) {
            switch (item?.type) {
              case 'spline':
                viewerRef.current?.drawArrowSpline(item.name, item.coord, item.groupCoord);
                break;
              case 'circle':
                viewerRef.current?.drawCircle(item.name, item.coord as [number, number], true);
                break;
              case 'image':
                await viewerRef.current?.drawImage(item.coord as number[], item.imageConfig);
                break;
            }
          }
          setTimeout(() => {
            currentRFAnimationRef.current = 'neck-rf';
            setCurrentRFAnimation(currentRFAnimationRef.current);
          }, 400);
        }

        //  DONE
        if (solution === 'abdomen-RF-dynamic') {
          for (const item of ModelSolution['abdomen-RF']) {
            switch (item?.type) {
              case 'spline':
                viewerRef.current?.drawArrowSpline(item.name, item.coord, item.groupCoord);
                break;
              case 'circle':
                viewerRef.current?.drawCircle(item.name, item.coord as [number, number], true);
                break;
              case 'image':
                await viewerRef.current?.drawImage(item.coord as number[], item.imageConfig);
                break;
            }
          }
          setTimeout(() => {
            currentRFAnimationRef.current = 'abdomen-rf';
            setCurrentRFAnimation(currentRFAnimationRef.current);
          }, 400);
        }

        // DONE
        if (solution === 'back-RF-dynamic') {
          for (const item of ModelSolution['back-RF']) {
            switch (item?.type) {
              case 'spline':
                viewerRef.current?.drawArrowSpline(item.name, item.coord, item.groupCoord);
                break;
              case 'circle':
                viewerRef.current?.drawCircle(item.name, item.coord as [number, number], true);
                break;
              case 'image':
                await viewerRef.current?.drawImage(item.coord as number[], item.imageConfig);
                break;
            }
          }
          setTimeout(() => {
            currentRFAnimationRef.current = 'back-rf';
            setCurrentRFAnimation(currentRFAnimationRef.current);
          }, 400);
        }

        // DONE
        if (solution === 'matrix-moxibustion-dynamic') {
          const currentRunningItem = config?.find(item => item.state === 'RUNNING') as any;

          const areaId2Coord = {
            1: [240, 220],
            2: [240, 285],
            3: [240, 368],
            4: [240, 430],
            5: [240, 475],
          };

          setCurrentActivePointCoords({
            coord: areaId2Coord[currentRunningItem?.target?.areaId],
            isLeft: true,
            info: currentRunningItem,
          });
          currentMoxActiveIdxRef.current = currentRunningItem?.target?.areaId;
          setCurrentMoxActiveIdx(currentMoxActiveIdxRef.current);
        }

        // DONE
        if (solution === 'matrix-moxibustion-abdomen-dynamic') {
          const currentRunningItem = config?.find(item => item.state === 'RUNNING') as any;
          const areaId2Coord = {
            6: [210, 445],
            7: [210, 470],
          };
          areaId2Coord[currentRunningItem?.target?.areaId] &&
            setCurrentActivePointCoords({
              coord: areaId2Coord[currentRunningItem?.target?.areaId],
              isLeft: true,
              info: currentRunningItem,
            });

          currentMoxActiveIdxRef.current = currentRunningItem?.target?.areaId;
          setCurrentMoxActiveIdx(currentMoxActiveIdxRef.current);
        }
      },
      [drawMassageActivePoint, handleRenderMassageNodule]
    );

    const renderModelSolution = useCallback(
      async (solution: Massage.ModelSolution | Massage.DynamicSolution, tryTimes = 0) => {
        if (!viewerRef.current) return;

        if (!solution || tryTimes > 300 || !latestCustomerGender.current) return;

        if (!hasInitViewer.current) {
          setTimeout(() => renderModelSolution(solution, ++tryTimes), 100);
          return;
        }

        // 判断需要加载哪个人体模型，以及需要切换人体模型的时机
        if (solution.includes('abdomen')) {
          if (currentBodyImageRef.current !== 'front') {
            currentBodyImageRef.current = 'front';
            await viewerRef.current?.changeImageLayer(IAMGE_URL_FRONT[latestCustomerGender.current]);
          }
        } else {
          if (currentBodyImageRef.current !== 'back') {
            currentBodyImageRef.current = 'back';
            await viewerRef.current?.changeImageLayer(IAMGE_URL_BACK[latestCustomerGender.current]);
          }
        }

        // 当前仅渲染模型底图，忽略渲染模型贴图和按摩点
        if (onlyRenderSolutionBgRef.current) {
          setIsRenderOk(true);
          onRenderModelOK?.();
          return;
        }
        if (!solution.includes('dynamic')) {
          if (ModelSolution[solution]?.length) {
            for (const item of ModelSolution[solution]) {
              switch (item?.type) {
                case 'spline':
                  viewerRef.current?.drawCircleLineWithPixelCoord(item.coord, {
                    opacity: 1,
                    stroke: '#abc4f5',
                    lineId: item.id,
                  });
                  break;
                case 'image':
                  await viewerRef.current?.drawImage(item.coord as number[], item.imageConfig);
                  break;
              }
            }
          }

          // 绘制按摩点位
          if (!!SOLUTION_TO_MASSAGE_POINT[solution]) {
            SOLUTION_TO_MASSAGE_POINT[solution].forEach((item: { coord: any[] }) => {
              viewerRef.current?.drawImage(item.coord, { url: MassagePointImg, size: MassagePointSize });
            });
          }
        }
        setIsRenderOk(true);
        onRenderModelOK?.();
      },
      [latestCustomerGender, onRenderModelOK, onlyRenderSolutionBgRef, setIsRenderOk]
    );

    // 格式化自定义画线坐标，使其渲染在模型内
    const formatCustomLineModelCoords = useCallback((coords: number[]) => {
      if (!coords?.length) return [];

      return coords?.map((item, index) => {
        // X坐标
        if (index % 2 === 0) {
          return item < 0 ? 0.02 : item > 1 ? 0.98 : item;
        } else {
          return Math.min(item || 0, 0.99);
        }
      });
    }, []);

    // 渲染自定义画线
    const renderCustomLineCoords = useCallback(
      (
        coords: Array<{
          lineId: string;
          state: 'RUNNING' | 'PENDING' | 'DONE';
          coord: number[];
        }>
      ) => {
        if (!viewerRef.current) return;

        if (isRenderedCustomLine.current || !coords?.length) return;

        coords?.forEach(item => {
          viewerRef.current?.drawDottedCircleLineInModel(formatCustomLineModelCoords(item.coord), {
            stroke: '#84a6eb',
            lineId: item.lineId,
          });

          if (item.state === 'DONE') {
            viewerRef.current?.highlightDottedCircleLineInModel(item.lineId);
          } else if (item.state === 'RUNNING') {
            viewerRef.current?.activeDottedCircleLineInModel(item.lineId);
          }
        });
        isRenderedCustomLine.current = true;
      },
      [formatCustomLineModelCoords]
    );

    useImperativeHandle(
      ref,
      () => ({
        /** 截图 */
        screenshot: () => viewerRef.current?.screenshot(),
        /** 高亮结节 */
        highlightNodePoint: (pointId: string) => viewerRef.current?.highlightNodePoint(pointId),
        /** 使结节置为动态效果  */
        activeNodePoint: (pointId: string) => viewerRef.current?.activeNodePoint(pointId),
        /** 清除当前结节的高亮状态 */
        clearActiveNode: () => viewerRef.current?.clearActiveNode(),
        /** 重置结节高亮状态 */
        resetNodeHighlight: () => viewerRef.current?.resetNodeHighlight(),
        /** 重置线的高亮状态 */
        resetSplineHighlight: () => viewerRef.current?.resetSplineHighlight(),
        /** 清除所有结节 */
        clearAllPoints: () => viewerRef.current?.clearAllPoints(),
        /** 动态设置矩阵式艾灸区域 */
        setMatrixMoxibustionArea,
        /**防止用户熄屏状态在打开屏幕 动效还是旧的 复用了内存，恢复默认值 */
        resetDynamicSolution: () => {
          isRenderedCustomLine.current = false;
          isRenderedPreSetLine.current = false;
        },
        drawDynamicSolution,
        renderCustomLineCoords,
        activeCustomLine: (lineId: string) => {
          setShockHitedTargetId(lineId);
          viewerRef.current?.activeDottedCircleLineInModel(lineId);
        },
        handleRenderMassageNodule,
        renderPreSetSplineHitInfo,
      }),
      [
        drawDynamicSolution,
        handleRenderMassageNodule,
        renderCustomLineCoords,
        setMatrixMoxibustionArea,
        renderPreSetSplineHitInfo,
      ]
    );

    const formatLabelCoords = useMemo(() => {
      if (!currentActivePointCoord?.coord?.length) {
        return {
          top: '-300px',
          right: '-300px',
        };
      }

      const currentTargetType = currentActivePointCoord?.info?.target?.type;

      if (currentActivePointCoord?.isLeft) {
        return {
          top: `${
            currentActivePointCoord?.coord[1] -
            (currentTargetType === 'ACUPOINT' ? 12 : currentTargetType === 'NODULE' ? 18 : 28)
          }px`,
          right: `${(currentTargetType === 'NODULE' ? 596 : 585) - currentActivePointCoord?.coord[0]}px`,
        };
      } else {
        return {
          top: `${
            currentActivePointCoord?.coord[1] -
            (currentTargetType === 'ACUPOINT' ? 12 : currentTargetType === 'NODULE' ? 16 : 28)
          }px`,
          left: `${currentActivePointCoord?.coord[0] + (currentTargetType === 'NODULE' ? 35 : 30)}px`,
        };
      }
    }, [currentActivePointCoord]);

    // 渲染模型时防抖，防止两次渲染间隙过短
    const renderModelSolutionWithThrottle = useMemo(
      () =>
        debounce((modelSolution: Massage.ModelSolution | Massage.DynamicSolution) => {
          viewerRef.current?.clearAllDrawLayer();
          renderModelSolution(modelSolution);
        }, 350),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    useEffect(() => {
      // 人工单不显示艾灸和射频动效
      if (modelSolution === 'normal-manual') {
        setCurrentMoxActiveIdx(null);
        setCurrentRFAnimation(null);
        return;
      }

      if (['Finishing', 'Finished', 'Pausing', 'Paused'].includes(massageState)) {
        setCurrentMoxActiveIdx(null);
        setCurrentRFAnimation(null);
      } else if (['Running_Massaging', 'Running'].includes(massageState)) {
        setCurrentMoxActiveIdx(currentMoxActiveIdxRef.current);
        setCurrentRFAnimation(currentRFAnimationRef.current);
      }
    }, [massageState, modelSolution]);

    // 将渲染方法防抖，防止两次渲染间隔太短导致的drawer数据异常问题
    useEffect(() => {
      if (currentServingOrder?.customerGender) {
        renderModelSolutionWithThrottle(modelSolution);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelSolution, currentServingOrder?.customerGender]);

    useEffect(() => {
      currentServingOrder?.customerGender && init(currentServingOrder.customerGender);
      return () => {
        viewerRef.current?.destroy();
        viewerRef.current = null;
        allHighlightNodeInfo.current?.clear?.();
        if (delayDrawTimer.current?.length) {
          delayDrawTimer.current.forEach(timer => clearTimeout(timer));
          delayDrawTimer.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentServingOrder?.customerGender]);

    // 渲染模型结节
    useEffect(() => {
      if (!bodyNodeList?.length) {
        onRenderPointOK?.();
        return;
      }
      if (isRenderOk && PLAN_SHOULD_RENDER_NODE_POINT.includes(modelSolution as Massage.ModelSolution)) {
        renderBodyPoints(bodyNodeList);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bodyNodeList, modelSolution, isRenderOk]);

    return (
      <div className={cx('model-2d', { 'is-matrix-mox': isMatrixMox })}>
        {!!currentActivePointCoord?.coord?.length && currentMassageType === 'Massage' && (
          <div
            style={{
              ...formatLabelCoords,
              ...(+formatLabelCoords?.top?.replace('px', '') > 470
                ? // label高度超出div容器，限制高度的同时计算label箭头的位置
                  { top: '470px', '--arrow-top': `${+formatLabelCoords?.top?.replace('px', '') - 470 + 8}px` }
                : {}),
            }}
            className={cx('massage-label', {
              'left-arrow': !currentActivePointCoord?.isLeft,
              'short-arrow': ['ACUPOINT', 'NODULE'].includes(currentActivePointCoord?.info?.target?.type),
              'is-intensify': isInIntensifying,
              'has-effect-intro': !!effectDesc,
            })}
          >
            {(currentActivePointCoord?.info?.target?.type === 'ACUPOINT' ||
              currentActivePointCoord?.info?.target?.type === 'NODULE') && (
              <>
                <div className={cx('header')}>
                  <div className={cx('left')}>
                    <div className={cx('acupoint-name')}>
                      {isInIntensifying ? '加强' : (currentActivePointCoord?.info?.target as any)?.acupointName}
                    </div>
                  </div>
                  <div className={cx('right')}>
                    <div className={cx('acupoint-name')}>{allValues['robot.force']}N</div>
                  </div>
                </div>
                <When condition={!!effectDesc}>
                  <div className={cx('effect-desc', 'point-desc')}>{effectDesc}</div>
                </When>
              </>
            )}
            {currentActivePointCoord?.info?.target?.type === 'MATRIX_MOXIBUSTION_AREA' && (
              <>
                <div className={cx('header')}>
                  <div className={cx('left')}>
                    <div className={cx('acupoint-name')}>{currentActivePointCoord?.info?.actionName}</div>
                  </div>
                  <div className={cx('right')}>
                    <div className={cx('acupoint-name')}>
                      {currentActivePointCoord?.info?.target?.duration / 60}分钟
                    </div>
                  </div>
                </div>
                <When condition={!!effectDesc && !isInIntensifying}>
                  <div className={cx('effect-desc')}>{effectDesc}</div>
                </When>
              </>
            )}
            {currentActivePointCoord?.info?.target?.type === 'ACUPOINT_ROUTE' && (
              <>
                <div className={cx('header', 'is-acupoint-route')}>
                  <div className={cx('left')}>
                    <div className={cx('acupoint-name')}>
                      {isInIntensifying ? '加强' : currentActivePointCoord?.info?.target?.starting?.acupointName}
                    </div>
                    {!isInIntensifying && (
                      <>
                        <Icon className={cx('icon')} name="start" color="#0055FF" />
                        <div className={cx('acupoint-name')}>
                          {currentActivePointCoord?.info?.target?.ending?.acupointName}
                        </div>
                      </>
                    )}
                  </div>
                  <div className={cx('right')}>
                    {allValues['force_range']?.[0] === 0 && allValues['force_range']?.[1] === 0 ? (
                      <div className={cx('acupoint-name')}>{allValues['robot.force']}N</div>
                    ) : (
                      <>
                        <div className={cx('acupoint-name')}>{allValues['force_range']?.[0]}N</div>
                        <Icon className={cx('icon')} name="start" color="#0055FF" />
                        <div className={cx('acupoint-name')}>{allValues['force_range']?.[1]}N</div>
                      </>
                    )}
                  </div>
                </div>
                <When condition={!!effectDesc && !isInIntensifying}>
                  <div className={cx('effect-desc')}>{effectDesc}</div>
                </When>
              </>
            )}
          </div>
        )}
        {!!leftLoopTips?.times && (
          <div
            className={cx('loop-tips-left')}
            style={{ left: `${leftLoopTips.left}px`, top: `${leftLoopTips.top}px` }}
          >
            {leftLoopTips.times}
          </div>
        )}
        {!!rightLoopTips?.times && (
          <div
            className={cx('loop-tips-right')}
            style={{ left: `${rightLoopTips.left}px`, top: `${rightLoopTips.top}px` }}
          >
            {rightLoopTips.times}
          </div>
        )}
        <RFAnimation animationType={currentRFAnimation} />
        {!!currentBuffetTip?.name && (
          <div className={cx('buffet-tip', { isRight: currentBuffetTip?.name?.includes('右') })}>
            <div className={cx('buffet-tip-box')}>
              <div className={cx('acu-name')}>{currentBuffetTip?.name}：</div>
              <div className={cx('tip-box')}>
                <div className={cx('tip')}>{currentBuffetTip?.tip}</div>
              </div>
            </div>
          </div>
        )}
        <MoxAnimation activeIdx={currentMoxActiveIdx} />
        <When condition={isRenderOk}>
          <PlainLabel shockHitedTargetId={shockHitedTargetId} />
        </When>
        <div ref={containerRef} id="model-2d-viewer" />
      </div>
    );
  }
);

export default Model2D;
